import * as React from 'react';
import { LazyModule, LazyImport } from 'owa-bundling-light';
import useLazyImport from './utils/useLazyImport';
import canComponentAcceptRef from './utils/canComponentAcceptRef';
import BroadComponentType from './types/BroadComponentType';

/**
 * Return component type of createLazyComponent
 *
 * Uses the common props of the lazy & placeholder component,
 * with a ref to one of the two components, depending on if the
 * inner component has been loaded yet.
 */
export type LazyComponent<
    TComponentProps,
    TPrimaryComponentRef,
    TPlaceholderComponentRef = TPrimaryComponentRef
> = React.ForwardRefExoticComponent<
    React.PropsWithChildren<React.PropsWithoutRef<TComponentProps>> &
        React.RefAttributes<
            TPlaceholderComponentRef extends void
                ? TPrimaryComponentRef
                : TPrimaryComponentRef | TPlaceholderComponentRef
        >
>;

// For usage of this API, see the owa-bundling README
function createLazyComponent<TProps, TModule, TInnerRef, TPlaceholderRef = void>(
    lazyModule: LazyModule<TModule>,
    getter: (module: TModule) => BroadComponentType<TProps, TInnerRef>,
    placeholder?: BroadComponentType<TProps, TPlaceholderRef>,
    importErrorHandler?: (reason: any) => void
): LazyComponent<TProps, TInnerRef, TPlaceholderRef> {
    let lazyImport = new LazyImport(lazyModule, getter);

    return React.forwardRef(function LazyComponent(
        props: TProps,
        ref: React.Ref<TInnerRef | TPlaceholderRef>
    ) {
        const InnerComponet:
            | BroadComponentType<TProps, TInnerRef>
            | BroadComponentType<TProps, TPlaceholderRef>
            | null = useLazyImport(lazyImport, placeholder, importErrorHandler);

        return !InnerComponet ? null : canComponentAcceptRef(InnerComponet) ? (
            <InnerComponet ref={ref} {...props} />
        ) : (
            <InnerComponet {...props} />
        );
    });
}
export default createLazyComponent;
